import {
    PRIMITIVE_LINES,
    Entity,
    Mesh,
    MeshInstance,
    ShaderMaterial,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import { isSceneCamera, getSceneCameraData } from './scene-camera';
import { vertexShader, fragmentShader } from './shaders/debug-shader';

const tmpForward = new Vec3();
const tmpRight = new Vec3();
const tmpUp = new Vec3();
const tmpBase = new Vec3();
const tmpTL = new Vec3();
const tmpTR = new Vec3();
const tmpBL = new Vec3();
const tmpBR = new Vec3();
const tmpUpTip = new Vec3();

// lines per camera icon: 4 pyramid + 4 base rect + 2 up indicator = 10
const LINES_PER_CAMERA = 10;
const VERTS_PER_CAMERA = LINES_PER_CAMERA * 2;

// Orange for unselected, white for selected
const COLOR_UNSELECTED = { r: 255, g: 165, b: 0, a: 255 };
const COLOR_SELECTED = { r: 255, g: 255, b: 255, a: 255 };

class SceneCameraGizmos extends Element {
    entity: Entity;
    mesh: Mesh;
    material: ShaderMaterial;
    meshInstance: MeshInstance;
    dirty = true;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const scene = this.scene;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'sceneCameraGizmoMaterial',
            vertexGLSL: vertexShader,
            fragmentGLSL: fragmentShader
        });
        this.material.depthWrite = true;
        this.material.depthTest = true;
        this.material.update();

        this.mesh = new Mesh(device);
        this.mesh.primitive[0] = {
            baseVertex: 0,
            type: PRIMITIVE_LINES,
            base: 0,
            count: 0
        };

        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('sceneCameraGizmos');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.worldLayer.id]
        });

        scene.app.root.addChild(this.entity);

        const markDirty = () => {
            this.dirty = true;
            scene.forceRender = true;
        };
        const { events } = scene;
        events.on('hierarchy.changed', markDirty);
        events.on('selection.changed', markDirty);
        events.on('scene.elementRemoved', markDirty);
        events.on('helper.visibility', markDirty);
    }

    destroy() {
        this.entity?.destroy();
    }

    onPreRender() {
        const { scene } = this;

        // Always rebuild when dirty (scene cameras are always visible)
        // The mesh will simply be empty if there are no cameras
        this.dirty = true; // cameras can be transformed every frame via gizmo

        this.entity.enabled = scene.camera.renderOverlays;

        if (this.entity.enabled) {
            this.rebuildMesh();
        }
    }

    private rebuildMesh() {
        const { scene } = this;
        const helpers = scene.getElementsByType(ElementType.helper);
        const cameras = helpers.filter(h => isSceneCamera(h) && (h as any).visible);

        if (cameras.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        const selection = scene.events.invoke('selection');

        const boundSize = scene.bound.halfExtents.length();
        const iconScale = boundSize > 0 ? boundSize * 0.04 : 0.2;

        const depth = iconScale * 2;
        const halfW = iconScale * 1.2;
        const halfH = iconScale * 0.9;

        const numVerts = cameras.length * VERTS_PER_CAMERA;
        const positions: number[] = [];
        const colors = new Uint8Array(numVerts * 4);

        let vertOffset = 0;

        const pushLine = (a: Vec3, b: Vec3) => {
            positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
        };

        for (const cam of cameras) {
            const data = getSceneCameraData(cam);

            // Skip camera currently in look-through mode
            if (data?.lookThrough) continue;

            const entity = (cam as any).entity;
            const position: Vec3 = entity.getPosition();
            const rotation = entity.getRotation();

            // Forward direction from entity rotation
            rotation.transformVector(Vec3.FORWARD, tmpForward);
            rotation.transformVector(Vec3.RIGHT, tmpRight);
            rotation.transformVector(Vec3.UP, tmpUp);

            // Base center (in front of camera position)
            tmpBase.copy(position).addScaled(tmpForward, depth);

            // Frustum corners
            tmpTL.copy(tmpBase).addScaled(tmpUp, halfH).addScaled(tmpRight, -halfW);
            tmpTR.copy(tmpBase).addScaled(tmpUp, halfH).addScaled(tmpRight, halfW);
            tmpBL.copy(tmpBase).addScaled(tmpUp, -halfH).addScaled(tmpRight, -halfW);
            tmpBR.copy(tmpBase).addScaled(tmpUp, -halfH).addScaled(tmpRight, halfW);

            // Pyramid edges from position to corners
            pushLine(position, tmpTL);
            pushLine(position, tmpTR);
            pushLine(position, tmpBL);
            pushLine(position, tmpBR);

            // Base rectangle
            pushLine(tmpTL, tmpTR);
            pushLine(tmpTR, tmpBR);
            pushLine(tmpBR, tmpBL);
            pushLine(tmpBL, tmpTL);

            // Up indicator triangle
            tmpUpTip.copy(tmpBase).addScaled(tmpUp, halfH * 1.5);
            pushLine(tmpTL, tmpUpTip);
            pushLine(tmpTR, tmpUpTip);

            // Color: white if selected, orange if not
            const isSelected = cam === selection;
            const clr = isSelected ? COLOR_SELECTED : COLOR_UNSELECTED;
            for (let i = 0; i < VERTS_PER_CAMERA; i++) {
                const off = (vertOffset + i) * 4;
                colors[off] = clr.r;
                colors[off + 1] = clr.g;
                colors[off + 2] = clr.b;
                colors[off + 3] = clr.a;
            }
            vertOffset += VERTS_PER_CAMERA;
        }

        // If all cameras were skipped (e.g. all in look-through), hide
        if (positions.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        // Trim colors array if some cameras were skipped
        const actualVerts = positions.length / 3;
        const actualColors = actualVerts > 0 ? colors.subarray(0, actualVerts * 4) : colors;

        this.mesh.setPositions(positions);
        this.mesh.setColors32(actualColors);
        this.mesh.update(PRIMITIVE_LINES);
    }
}

export { SceneCameraGizmos };
